import type { ContinentItem, CountryItem } from './data';
import { translations } from './generated/translations';
import locales from './resources/locales.json';

export const defaultLocale = 'en';
const typedLocales = locales as ContinentItem[];
const PIWIK_ID_WHITE = '632a67bb-026d-4e78-af5e-e4f960a7413a';

// list of all locales that we want to support in our application.
export const supportedLocales: string[] = typedLocales.flatMap(continent =>
  continent.countries.flatMap(country => country.languages.map(language => language.code))
);

export const generateFallbackLng = (initialLocales: string[]) =>
  initialLocales.reduce(
    (fallbacks, locale) => {
      if (locale === defaultLocale) {
        return fallbacks;
      }

      const fallbackChain = [defaultLocale];
      const lang = locale.split('-')[0];
      if (lang !== locale) {
        // place before the default locale if not existing yet
        if (!fallbackChain.includes(lang) && translations[lang]) {
          fallbackChain.unshift(lang);
        }
      }

      return { ...fallbacks, [locale]: fallbackChain };
    },
    { [defaultLocale]: [] }
  );

/**
 * returns a list of countryItems that are released
 * @param getDomainsReleasedForDev set true if domains that are released for dev / testing environments should be returned as well
 */
export const getReleasedCountries = (envName: string) =>
  typedLocales.flatMap(continent => [
    ...continent.countries.filter(country => {
      switch (envName) {
        case 'local':
          return true;
        case 'black':
          return false;
        case 'aws':
        case 'white':
          return country.isWebtestReleased ?? country.isReleased;
        case 'orange':
        default:
          return country.isReleased;
      }
    }),
  ]);

/**
 * returns a list of domains that is released
 * @param getDomainsReleasedForDev set true if domains that are released for dev / testing environments should be returned as well
 */
export const getReleasedDomains = (envName: string) => {
  const countries = getReleasedCountries(envName);
  const domainsWithDuplicates = countries.map(country => country.baseUrl);
  // remove duplicates from domainsWithDuplicates
  const domains = domainsWithDuplicates.filter((domain, index) => domainsWithDuplicates.indexOf(domain) === index);
  return domains;
};

export const getCountriesForDomain = (domain: string) =>
  typedLocales.flatMap(continent => continent.countries.filter(country => country.baseUrl === domain));

export const getDefaultLocaleForDomain = (domain: string) => {
  const countriesWithDomain = getCountriesForDomain(domain);

  if (countriesWithDomain.length === 1) {
    // if the domain is used for a country with multiple languages (e.g. Switzerland, Belgium), use the defaultLocale attribute
    if (countriesWithDomain[0].languages.length > 1) {
      return countriesWithDomain[0].defaultLocale || defaultLocale;
    }
    // if the domain is used for a country with only one language (e.g. Germany, France), use the languages code
    return countriesWithDomain[0].languages[0].code;
  }
  // in all other cases (e.g. multiple countries for same domain like in igus.eu) use en
  return defaultLocale;
};

export const getLocalesForDomain = (domain: string) =>
  getCountriesForDomain(domain)
    .flatMap(country => country.languages.map(language => language.code))
    .filter(locale => locale !== getDefaultLocaleForDomain(domain));

export const getCountryForLocale = (locale: string): CountryItem | undefined =>
  typedLocales.flatMap(continent =>
    continent.countries.filter((country: CountryItem) => country.languages.some(language => language.code === locale))
  )[0] || undefined;

/**
 * Retrieves the base URL and available language locales for a given country.
 *
 * @param {string | undefined} countryCode - The ISO country code used to identify the country.
 * If countryCode is undefined, the returned locales fall back to the locales available for igus.eu.
 * This is because igus.eu is defined as the only "country" without a countryCode in locales.json.
 *
 * @returns {{ baseUrl: string, locales: string[] }} An object containing:
 * - `baseUrl` (string): The base URL for the given country or an empty string if no country is found.
 * - `locales` (string[]): A list of language codes associated with the country or an empty array if no languages are found.
 */

export const getLocalesForCountry = (countryCode: string | undefined): { baseUrl: string; locales: string[] } => {
  const country = typedLocales
    .flatMap(continent => continent.countries)
    .find(countryItem => countryItem.countryCode === countryCode);

  return { baseUrl: country?.baseUrl ?? '', locales: country?.languages.map(language => language.code) ?? [] };
};

export const isGtmEnabledForLocale = (locale: string): boolean => getCountryForLocale(locale)?.isGtmEnabled ?? true;

export const getPiwikId = (envName: string, locale: string): string | undefined => {
  switch (envName) {
    case 'white':
      return PIWIK_ID_WHITE;
    case 'orange':
      return getCountryForLocale(locale)?.piwikId ?? undefined;
    default:
      return undefined;
  }
};

export const isVATEnabledForLocale = (locale: string): boolean => getCountryForLocale(locale)?.isVATDisplayed ?? false;

export const getDomainsWithMultipleLanguages = (): string[] =>
  typedLocales.reduce<string[]>((acc, region) => {
    const multiLanguageDomains = region.countries.reduce<string[]>((countryAcc, country) => {
      if (country.languages.length > 1) {
        countryAcc.push(country.baseUrl.replace('www.', ''));
      }
      return countryAcc;
    }, []);
    return acc.concat(multiLanguageDomains);
  }, []);
